import { useEffect, useMemo, useState } from "react";

// react hook form
import { Controller, useForm } from "react-hook-form";

// mui
import {
  FormGroup,
  Grid,
  RadioGroup,
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  Alert,
  Modal,
} from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { useLocation, useNavigate } from "react-router-dom";

// hooks
import { useQuery } from "hooks/useQuery";

// consts
import { TERMS_OF_USE } from "components/constants/payment-link";
import {
  PREFIX,
  INPUT_INFOUSER_NAME,
  fnValidationRequest,
} from "../helpers/misc";

// shareds
import AutocompleteControlled from "components/Controllers/Autocomplete";
import CheckboxControlled from "components/Controllers/Checkbox";

// components
import UserInfo from "./UserInfo";
import PSEForm from "./PSEForm";
import PurchaseSummary from "./PurchaseSummary";
import CARDForm from "./CARDForm";

// scheme
import { yupResolver } from "@hookform/resolvers/yup";
import { paymentScheme } from "../scheme/paymentScheme";

// services
import {
  getInfoProductShopping,
  getInfoUserShoppingExpress,
  postPaymentShopping,
} from "services/shoppingExpress";
import { getVenueByCity } from "services/qrheadquarters";

import { fnResetFields, fnSettingValueUser } from "../helpers/resetFields";
import { fnBodyToSend } from "../helpers/datasend";
import { fnPaymentShoppign } from "../helpers/paymentShopping";
import { format } from "date-fns";

const Form = (props) => {
  const {
    documents,
    setMessageError,
    cities,
    infoProduct,
    setInfoProduct,
    infoUser,
  } = props;

  // query
  const query = useQuery();
  const uuid = query.get("uuid");
  const currentBrand = query.get("b");
  const productId = query.get("prod");
  const promId = query.get("prom");

  // navigation
  const navigate = useNavigate();

  // useStates
  const [loading, setLoading] = useState(false);
  const [samePersonPay, setSamePersonPay] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [disableInputs, setDisableInputs] = useState(true);
  const [venues, setVenues] = useState([]);
  const [loadingVenue, setLoadingVenue] = useState(false);

  const [changeVenue, setChangeVenue] = useState(false);

  // modal
  const [openModal, setOpenModal] = useState({
    open: false,
    data: {},
  });

  // react hook form
  const {
    handleSubmit,
    watch,
    control,
    formState: { isValid, errors, isSubmitting, isValidating },
    setValue,
    getValues,
    resetField,
    reset,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: { payment_type: "card" },
    resolver: yupResolver(paymentScheme),
    mode: "onChange",
  });

  const onSubmit = async (values) => {
    try {
      values.execute_at = format(new Date(values.execute_at), "yyyy-MM-dd", {
        locale: es,
      });
      const body = fnBodyToSend(values, infoProduct, productId || promId, uuid);
      const { data } = await postPaymentShopping(body);
      fnPaymentShoppign(data, setError, setOpenModal);
    } catch (err) {
      console.error(err);
    }
  };

  const onError = (err) => {
    console.error(err);
  };

  // useEffect putting same data to possible person to pay if box check is true
  useEffect(() => {
    if (samePersonPay) {
      INPUT_INFOUSER_NAME.forEach((name) => {
        setValue(`${PREFIX}_${name}`, getValues(name), {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    }
  }, [samePersonPay, watch("terms_of_use")]);

  // clearing data to possible person to pay if box check is false
  useEffect(() => {
    if (!samePersonPay) {
      INPUT_INFOUSER_NAME.forEach((name) => {
        setValue(`${PREFIX}_${name}`, null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    }
    //setValue("terms_of_use", false, {shouldValidate: true});
    clearErrors([
      "possible_customer_document_number",
      "possible_customer_document_type",
      "possible_customer_firstname",
      "possible_customer_lastname",
    ]);
  }, [samePersonPay]);

  // setter info user
  useEffect(() => {
    if (infoUser && documents) {
      fnSettingValueUser(documents, infoUser, setValue, setInputValue);
    }
  }, [infoUser, documents]);

  // get product info by id
  useEffect(() => {
    const venue = watch("customer_venue");
    const getInfoProd = async () => {
      try {
        clearErrors("customer_venue");
        const { data } = await getInfoProductShopping(
          uuid,
          productId || promId,
          venue?.id,
          productId ? "prod" : "prom"
        );
        fnValidationRequest(data) && setInfoProduct(data.data);
      } catch (err) {
        const error =
          err?.response?.data.message[0]?.message || "Escoja otra sede";
        setError("customer_venue", { message: error });
        setInfoProduct((prev) => {
          return { product: prev["product"] };
        });
      }
    };
    Boolean(venue) && getInfoProd();
  }, [uuid, productId, watch("customer_venue"), promId]);

  // get venue by city id
  useEffect(() => {
    const fnGetVenue = async () => {
      try {
        setLoadingVenue(true);
        const currentCity = getValues("customer_city");
        if (currentCity) {
          const { data } = await getVenueByCity(currentCity?.id);
          fnValidationRequest(data) && setVenues(data?.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingVenue(false);
      }
    };
    fnGetVenue();
  }, [changeVenue]);

  useEffect(() => {
    resetField("customer_venue");
    setInfoProduct((prev) => {
      return { product: prev["product"] };
    });
  }, [changeVenue]);

  useEffect(() => {
    reset(
      {
        payment_type: watch("payment_type") === "card" ? "card" : "pse",
      },
      { keepValues: true }
    );
  }, [watch("payment_type")]);

  useEffect(() => {
    clearErrors("payment_sending");
  }, [isValidating]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      {/* start Modal when payment is success  */}
      <Modal
        open={openModal.open}
        //onClose={() => setOpenModal((prev) => !prev.open)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableEscapeKeyDown
      >
        <Box sx={style}>
          <Box textAlign="center">
            <Box>
              <Typography variant="title" color="primary">
                ¡El pago fue exitoso!
              </Typography>
            </Box>
            <Button
              style={{
                background: "primary",
                marginTop: "10px",
                borderRadius: "10px",
                width: "auto",
              }}
              variant={"contained"}
              onClick={() => {
                navigate(
                  `/payment-shopping-express?uuid=${openModal?.data?.uuid}&b=${currentBrand}`
                );
              }}
            >
              VER DETALLES
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* end Modal when payment is success  */}

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Grid container spacing={4} p={3}>
          <Grid item xs={12}>
            <UserInfo
              control={control}
              watch={watch}
              documents={documents}
              setSamePersonPay={setSamePersonPay}
              inputValue={inputValue}
              setInputValue={setInputValue}
              disableInputs={disableInputs}
              setDisableInputs={setDisableInputs}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="title" color="primary">
                Ciudad
              </Typography>
            </Box>
            <Box>
              <AutocompleteControlled
                label="Ciudad"
                name="customer_city"
                control={control}
                rules={{ required: true }}
                options={cities}
                onChange={(_, data) => {
                  setChangeVenue((prev) => !prev);
                  resetField("customer_venue");
                  setValue("customer_venue", null);
                  setValue(
                    "customer_city",
                    { id: data?.id, name: data?.name },
                    { shouldValidate: true }
                  );
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="title" color="primary">
                Sede
              </Typography>
            </Box>
            <Box>
              {loadingVenue ? (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress size={25} />
                </Box>
              ) : (
                <AutocompleteControlled
                  label="Sede"
                  name="customer_venue"
                  control={control}
                  rules={{ required: true }}
                  options={venues}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <PurchaseSummary information={infoProduct} currentBrand={currentBrand}/>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="title" color="primary">
              Inicio de plan
            </Typography>
            <Box>
              <Controller
                rules={{
                  required: true,
                }}
                control={control}
                name="execute_at"
                defaultValue={new Date()}
                render={({ field }) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={es}
                  >
                    <MobileDatePicker
                      disablePast
                      {...field}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          error={errors?.execute_at}
                          fullWidth
                        />
                      )}
                      label={"Fecha de inicio"}
                      placeholder={"Fecha de inicio"}
                      inputFormat="dd/MM/yyyy"
                      mask={"__/__/____"}
                    />
                  </LocalizationProvider>
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="title" color="primary">
              Metodo de pago
            </Typography>
            <Box>
              <Typography>
                Todas las transacciones se realizan de forma segura
              </Typography>
            </Box>
            <Box mt={3}>
              <RadioGroup
                row
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
                value={watch("payment_type")}
                onChange={() => fnResetFields(getValues, setValue)}
              >
                <CheckboxControlled
                  type="radio"
                  name="payment_type"
                  control={control}
                  value="card"
                  label="CRÉDITO / DÉBITO"
                />
                {props.currentBrand !== "3" && (
                  <CheckboxControlled
                    type="radio"
                    name="payment_type"
                    control={control}
                    value="pse"
                    label="PSE"
                  />
                )}
              </RadioGroup>
            </Box>
          </Grid>

          <Grid item xs={12}>
            {watch("payment_type") === "card" ? (
              <CARDForm
                control={control}
                setValue={setValue}
                documents={documents}
                samePersonPay={samePersonPay}
              />
            ) : (
              <PSEForm
                control={control}
                documents={documents}
                samePersonPay={samePersonPay}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormGroup>
              <CheckboxControlled
                name="terms_of_use"
                control={control}
                label={
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 12, textAlign: "justify" }}
                  >
                    {TERMS_OF_USE}
                  </Typography>
                }
              />
            </FormGroup>
          </Grid>

          {errors?.payment_sending && (
            <Grid item xs={12}>
              <Alert severity="error">{errors?.payment_sending?.message}</Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              disabled={
                !isValid ||
                loading ||
                Object.keys(errors).length ||
                isSubmitting
              }
              type="submit"
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : "Pagar"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default Form;
